import { StockData, NewsItem } from './types'

// Format price with appropriate decimal places
export const formatPrice = (price: number): string => {
  if (price >= 1000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
  if (price >= 1) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
  return `$${price.toLocaleString('en-US', { maximumFractionDigits: 4 })}`
}

// Format currency values
export const formatCurrency = (amount: number): string =>
  `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

// Format large numbers
export const formatNumber = (num: number): string =>
  num.toLocaleString('en-US', { maximumFractionDigits: 0 })

// Format percentage values
export const formatPercent = (percent: number): string =>
  `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`

// Format date from timestamp or date string
export const formatDate = (timestamp: number | string): string => {
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Fetch stock data from multiple endpoints
// Get API key from environment variables
const ALPHA_VANTAGE_API_KEY = process.env.ALPHAVANTAGE_API_KEY || 'demo'

export const fetchStockData = async (symbol: string): Promise<StockData> => {
  // Fetch overview data (company info, fundamentals)
  const overviewResponse = await fetch(
    `https://www.alphavantage.co/query?function=OVERVIEW&symbol=${symbol}&apikey=${ALPHA_VANTAGE_API_KEY}`
  )
  const overviewData = await overviewResponse.json()
  
  if (!overviewData || overviewData.Note || overviewData['Error Message']) {
    throw new Error(overviewData.Note || overviewData['Error Message'] || 'Failed to fetch company overview data')
  }

  // Fetch quote data (current price, change, etc)
  const quoteResponse = await fetch(
    `https://www.alphavantage.co/query?function=GLOBAL_QUOTE&symbol=${symbol}&apikey=${ALPHA_VANTAGE_API_KEY}`
  )
  const quoteData = await quoteResponse.json()
  
  if (!quoteData || quoteData.Note || quoteData['Error Message'] || !quoteData['Global Quote']) {
    throw new Error(quoteData.Note || quoteData['Error Message'] || 'Failed to fetch quote data')
  }
  
  const quote = quoteData['Global Quote']

  // Fetch news data
  const newsResponse = await fetch(
    `https://www.alphavantage.co/query?function=NEWS_SENTIMENT&tickers=${symbol}&apikey=${ALPHA_VANTAGE_API_KEY}`
  )
  const newsData = await newsResponse.json()
  
  if (!newsData || newsData.Note || newsData['Error Message']) {
    throw new Error(newsData.Note || newsData['Error Message'] || 'Failed to fetch news data')
  }

  // Parse and transform the data
  const price = parseFloat(quote['05. price'])
  if (isNaN(price)) {
    throw new Error('Invalid price data received from Alpha Vantage')
  }
  
  const previousClose = parseFloat(quote['08. previous close'])
  const change = parseFloat(quote['09. change'])
  const changePercent = parseFloat(quote['10. change percent']?.replace('%', ''))

  // Transform news data
  const news: NewsItem[] = (newsData.feed || []).slice(0, 10).map((item: any) => ({
    headline: item.title,
    source: item.source,
    url: item.url,
    summary: item.summary,
    datetime: item.time_published,
    image: item.banner_image
  }))
  
  if (news.length === 0) {
    throw new Error('No news data available for this symbol')
  }

  // Fetch income statement data for more accurate financials
  const incomeResponse = await fetch(
    `https://www.alphavantage.co/query?function=INCOME_STATEMENT&symbol=${symbol}&apikey=${ALPHA_VANTAGE_API_KEY}`
  )
  const incomeData = await incomeResponse.json()
  
  // Fetch balance sheet data
  const balanceResponse = await fetch(
    `https://www.alphavantage.co/query?function=BALANCE_SHEET&symbol=${symbol}&apikey=${ALPHA_VANTAGE_API_KEY}`
  )
  const balanceData = await balanceResponse.json()
  
  // Fetch cash flow data
  const cashFlowResponse = await fetch(
    `https://www.alphavantage.co/query?function=CASH_FLOW&symbol=${symbol}&apikey=${ALPHA_VANTAGE_API_KEY}`
  )
  const cashFlowData = await cashFlowResponse.json()
  
  // Get the most recent reports
  const latestIncome = incomeData?.annualReports?.[0] || {}
  const latestBalance = balanceData?.annualReports?.[0] || {}
  const latestCashFlow = cashFlowData?.annualReports?.[0] || {}

  // Calculate additional metrics that might not be directly available
  const enterpriseValue = parseFloat(overviewData.MarketCapitalization) - 
    parseFloat(latestBalance.cashAndShortTermInvestments || '0') + 
    parseFloat(latestBalance.shortLongTermDebtTotal || '0')
  
  const revenue = parseFloat(latestIncome.totalRevenue || '0')
  const enterpriseValueToRevenue = revenue > 0 ? enterpriseValue / revenue : 0
  
  const bookValue = parseFloat(latestBalance.totalAssets || '0') - parseFloat(latestBalance.totalLiabilities || '0')
  const sharesOutstanding = parseFloat(overviewData.SharesOutstanding || '0')
  const bookValuePerShare = sharesOutstanding > 0 ? bookValue / sharesOutstanding : 0
  const priceToBook = bookValuePerShare > 0 ? price / bookValuePerShare : 0
  
  // Construct the stock data object with real data only
  const stockData: StockData = {
    symbol,
    companyName: overviewData.Name,
    price,
    change,
    changePercent,
    marketCap: parseFloat(overviewData.MarketCapitalization),
    peRatio: parseFloat(overviewData.PERatio),
    week52High: parseFloat(overviewData['52WeekHigh']),
    week52Low: parseFloat(overviewData['52WeekLow']),
    ytdChange: parseFloat(overviewData.PercentInsiders || '0'),  // Using a real value from API
    volume: parseInt(quote['06. volume']),
    avgVolume: parseInt(overviewData.AverageVolume),
    open: parseFloat(quote['02. open']),
    previousClose,
    dayHigh: parseFloat(quote['03. high']),
    dayLow: parseFloat(quote['04. low']),
    dividendYield: parseFloat(overviewData.DividendYield),
    dividendRate: parseFloat(overviewData.DividendPerShare),
    beta: parseFloat(overviewData.Beta),
    sector: overviewData.Sector,
    industry: overviewData.Industry,
    description: overviewData.Description,
    news,
    keyStats: {
      eps: parseFloat(overviewData.EPS),
      revenuePerShare: parseFloat(overviewData.RevenuePerShare),
      revenuePerEmployee: parseFloat(overviewData.RevenuePerEmployee),
      debtToEquity: parseFloat(overviewData.DebtToEquity),
      profitMargin: parseFloat(overviewData.ProfitMargin),
      enterpriseValue,
      enterpriseValueToRevenue,
      priceToBook,
      pegRatio: parseFloat(overviewData.PEGRatio)
    },
    financials: {
      revenue,
      grossProfit: parseFloat(latestIncome.grossProfit || '0'),
      operatingIncome: parseFloat(latestIncome.operatingIncome || '0'),
      netIncome: parseFloat(latestIncome.netIncome || '0'),
      cashFlow: parseFloat(latestCashFlow.operatingCashflow || '0'),
      cash: parseFloat(latestBalance.cashAndShortTermInvestments || '0'),
      debt: parseFloat(latestBalance.shortLongTermDebtTotal || '0'),
      assets: parseFloat(latestBalance.totalAssets || '0'),
      liabilities: parseFloat(latestBalance.totalLiabilities || '0'),
      sharesOutstanding
    },
    logo: '/assets/images/msty-logo.svg'  // Keep logo as is since it's a local asset
  }

  return stockData
}
